/**
 * Card Variant Picker
 * Handles variant selection on product cards in collection pages
 */

document.addEventListener('submit', (e) => {
  console.log('Form submit detected:', e.target);
  console.log('Form ID:', e.target.id);
  console.log('Form action:', e.target.action);
}, true); // Use capture phase

class CardVariantPicker {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.attachEventListeners());
    } else {
      this.attachEventListeners();
    }
  }

  attachEventListeners() {
    // Handle radio button changes (color swatches)
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('card-variant-radio')) {
        e.preventDefault();
        e.stopPropagation();
        this.handleVariantChange(e.target);
      }
    });

    // Handle dropdown changes (size, etc.)
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('card-variant-select')) {
        e.preventDefault();
        e.stopPropagation();
        this.handleVariantChange(e.target);
      }
    });

    // Intercept form submissions to ensure variant ID is updated
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.dataset.type === 'add-to-cart-form') {
        const variantInput = form.querySelector('.product-variant-id');
        const card = form.closest('.recommendations-slide, .card-wrapper, li');
        
        if (card && variantInput) {
          const selectedVariantId = card.dataset.selectedVariantId;
          if (selectedVariantId && variantInput.value !== selectedVariantId) {
            variantInput.value = selectedVariantId;
          }
        }
      }
    }, true); // Use capture phase

    // Initialize all cards with their selected variants
    this.initializeAllCards();
  }

  initializeAllCards() {
    // Find all product cards on the page
    const cards = document.querySelectorAll('.card-wrapper');
    
    cards.forEach(card => {
      // Get the product ID from any variant element
      const variantElement = card.querySelector('[data-product-id]');
      if (!variantElement) return;
      
      const productId = variantElement.dataset.productId;
      
      // Get all selected options for this card
      const selectedOptions = this.getSelectedOptions(card, productId);
      
      // Get variants data
      const variantsScript = card.querySelector(`script[id*="variants-"][id*="${productId}"]`);
      if (!variantsScript) return;

      let variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Error parsing variants JSON:', e);
        return;
      }

      // Find matching variant based on initially selected options
      const matchingVariant = this.findMatchingVariant(variants, selectedOptions);
      
      if (matchingVariant) {
        // Initialize the card with the correct variant
        this.updateCard(card, matchingVariant, productId);
      }
    });
  }

  handleVariantChange(element) {
    const productId = element.dataset.productId;
    const card = element.closest('.card-wrapper');
    
    if (!card) return;

    // Update selected value display for radio buttons
    if (element.classList.contains('card-variant-radio')) {
      const optionPosition = element.dataset.optionPosition;
      const selectedSpan = card.querySelector(`[id*="selected-"][id*="-option${optionPosition}"]`);
      if (selectedSpan) {
        selectedSpan.textContent = element.value;
      }
    }

    // Get all selected options
    const selectedOptions = this.getSelectedOptions(card, productId);
    
    // Get variants data
    const variantsScript = card.querySelector(`script[id*="variants-"][id*="${productId}"]`);
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Error parsing variants JSON:', e);
      return;
    }

    // Find matching variant
    const matchingVariant = this.findMatchingVariant(variants, selectedOptions);
    
    if (matchingVariant) {
      this.updateCard(card, matchingVariant, productId);
    }
  }

  getSelectedOptions(card, productId) {
    const selectedOptions = [];

    // Get values from radio buttons (color swatches)
    const radioButtons = card.querySelectorAll('.card-variant-radio:checked');
    radioButtons.forEach(radio => {
      const position = parseInt(radio.dataset.optionPosition) - 1;
      selectedOptions[position] = radio.value;
    });

    // Get values from dropdowns
    const selects = card.querySelectorAll('.card-variant-select');
    selects.forEach(select => {
      const position = parseInt(select.dataset.optionPosition) - 1;
      selectedOptions[position] = select.value;
    });

    return selectedOptions;
  }

  findMatchingVariant(variants, selectedOptions) {
    return variants.find(variant => {
      return selectedOptions.every((value, index) => {
        const variantOption = variant[`option${index + 1}`];
        return variantOption === value;
      });
    });
  }

  updateCard(card, variant, productId) {
    // Store the selected variant ID on the card element for form submission
    const slideContainer = card.closest('.recommendations-slide');
    if (slideContainer) {
      slideContainer.dataset.selectedVariantId = variant.id;
    }
    card.dataset.selectedVariantId = variant.id;

    // Update hidden variant input
    let variantInput = card.querySelector('.product-variant-id');
    
    if (!variantInput) {
      if (slideContainer) {
        variantInput = slideContainer.querySelector('.product-variant-id');
      }
    }
    
    if (variantInput) {
      variantInput.value = variant.id;
      // Force a DOM update
      variantInput.setAttribute('value', variant.id);
    } else {
      console.error('Could not find variant input for product', productId);
    }

    this.updatePrice(card, variant, productId);

    // Update product image if variant has one
    if (variant.featured_image) {
      this.updateImage(card, variant, productId);
    }

    // Update button availability
    this.updateButton(card, variant);
  }

  updatePrice(card, variant, productId) {
    const priceContainer = card.querySelector(`[id*="price-"][id*="${productId}"]`);
    if (!priceContainer) return;

    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    
    let priceHTML = '';
    
    if (hasDiscount) {
      priceHTML = `
        <div class="price__container">
          <div class="price__sale">
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="price-item price-item--sale price-item--last">
              ${this.formatMoney(variant.price)}
            </span>
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span>
              <s class="price-item price-item--regular">
                ${this.formatMoney(variant.compare_at_price)}
              </s>
            </span>
          </div>
        </div>
      `;
    } else {
      priceHTML = `
        <div class="price__container">
          <div class="price__regular">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="price-item price-item--regular">
              ${this.formatMoney(variant.price)}
            </span>
          </div>
        </div>
      `;
    }
    
    priceContainer.innerHTML = priceHTML;
  }

  updateDiscountBadge(card, variant, productId) {
    const discountBadge = card.querySelector(`[id*="discount-badge-"][id*="${productId}"]`);
    if (!discountBadge) return;

    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    
    if (hasDiscount) {
      const discountPercentage = Math.round(
        ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
      );
      discountBadge.textContent = `${discountPercentage}% off`;
      discountBadge.style.display = '';
    } else {
      discountBadge.style.display = 'none';
    }
  }

  updateImage(card, variant, productId) {
    if (!variant.featured_image) return;

    const imgElement = card.querySelector(`[id*="product-image-"][id*="${productId}"] img`);
    if (imgElement) {
      // Get the image URL at appropriate size
      const imageUrl = variant.featured_image.src;
      const sizedImageUrl = this.getSizedImageUrl(imageUrl, '533x');
      
      imgElement.src = sizedImageUrl;
      imgElement.alt = variant.featured_image.alt || variant.name;
    }
  }

  updateButton(card, variant) {
    const submitButton = card.querySelector('.quick-add__submit');
    if (!submitButton) return;

    const buttonText = submitButton.querySelector('span:first-child');
    if (!buttonText) return;

    if (variant.available) {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-disabled');
      
      // Check if it's a pre-order
      const isPreorder = card.querySelector('[data-product-id]')?.closest('.card')?.querySelector('.badge')?.textContent?.includes('Pre-Order');
      
      if (isPreorder) {
        buttonText.textContent = window.theme?.strings?.preorder || 'Pre-Order';
      } else {
        buttonText.textContent = window.theme?.strings?.addToCart || 'Add to cart';
      }
    } else {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      buttonText.textContent = window.theme?.strings?.soldOut || 'Sold out';
    }
  }

  updateAvailableOptions(card, selectedVariant) {
    // This function would disable options that aren't available
    // based on the currently selected variant
    // For now, we'll keep it simple and just update based on availability
    
    const variantsScript = card.querySelector('script[id*="variants-"]');
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      return;
    }

    // Update radio buttons
    const radioButtons = card.querySelectorAll('.card-variant-radio');
    radioButtons.forEach(radio => {
      const optionPosition = parseInt(radio.dataset.optionPosition);
      const optionValue = radio.value;
      
      // Check if this option is available in any variant
      const isAvailable = variants.some(variant => {
        return variant[`option${optionPosition}`] === optionValue && variant.available;
      });
      
      radio.disabled = !isAvailable;
      
      const label = card.querySelector(`label[for="${radio.id}"]`);
      if (label) {
        if (isAvailable) {
          label.classList.remove('disabled');
        } else {
          label.classList.add('disabled');
        }
      }
    });

    // Update select options
    const selects = card.querySelectorAll('.card-variant-select');
    selects.forEach(select => {
      const optionPosition = parseInt(select.dataset.optionPosition);
      
      Array.from(select.options).forEach(option => {
        const optionValue = option.value;
        
        // Check if this option is available in any variant
        const isAvailable = variants.some(variant => {
          return variant[`option${optionPosition}`] === optionValue && variant.available;
        });
        
        option.disabled = !isAvailable;
      });
    });
  }

  formatMoney(cents) {
    // Check if Shopify's formatMoney function is available (most themes have this)
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Check if theme has a money format setting
    if (window.theme && window.theme.moneyFormat) {
      const dollars = (cents / 100).toFixed(2);
      return window.theme.moneyFormat.replace('{{amount}}', dollars).replace('{{amount_no_decimals}}', Math.round(cents / 100));
    }
    
    // Fallback to basic USD formatting
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    
    // Remove any existing size parameter
    const urlWithoutSize = url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\./g, '.');
    
    // Add new size parameter
    const extension = urlWithoutSize.split('.').pop();
    return urlWithoutSize.replace(`.${extension}`, `_${size}.${extension}`);
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.cardVariantPicker = new CardVariantPicker();
}

// Also initialize on Shopify section render (for dynamic sections)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (window.cardVariantPicker) {
      window.cardVariantPicker.attachEventListeners();
    }
  });
}